import React, { useState } from 'react';
import { authAPI } from '../../services/api';

/**
 * "Send a new verification link" — calls POST /api/auth/resend-verification (KAN-443).
 *
 * The server gives the same answer whether or not the address is registered, so this does
 * too: success and a "too many requests" reply both show the same neutral confirmation. A 429
 * comes either from the per-IP rate limit or from the server's two-minute throttle on a real
 * unverified account; telling them apart on screen would say the account exists.
 *
 * `labels` lets a page that uses the i18n file pass translated copy. The defaults are the plain
 * English used on pages that do not.
 */
export const DEFAULT_LABELS = {
  prompt: 'Enter your email address and we will send you a new verification link.',
  email: 'Email address',
  send: 'Send a new link',
  sending: 'Sending…',
  sent: 'If that address has an account that still needs verifying, we have sent a new link. It can take a few minutes to arrive. Check your spam folder too. If you asked recently, wait two minutes before asking again.',
  invalidEmail: 'Please enter a valid email address.',
  failed: 'We could not send that request. Please try again.',
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const ResendVerification = ({ initialEmail = '', labels = {} }) => {
  const L = { ...DEFAULT_LABELS, ...labels };
  const [email, setEmail] = useState(initialEmail);
  const [status, setStatus] = useState('idle'); // idle | sending | sent | error
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (status === 'sending') return; // a second click while the first is in flight
    const value = email.trim();
    if (!EMAIL_RE.test(value)) {
      setStatus('error');
      setError(L.invalidEmail);
      return;
    }
    setStatus('sending');
    setError('');
    try {
      await authAPI.resendVerification(value);
      setStatus('sent');
    } catch (err) {
      if (err.response?.status === 429) {
        setStatus('sent'); // same message as success: see the note above
      } else {
        setStatus('error');
        setError(L.failed);
      }
    }
  };

  const sending = status === 'sending';

  return (
    <form onSubmit={handleSubmit} noValidate style={{ margin: '1.5rem auto', maxWidth: 420, textAlign: 'left' }}>
      <p style={{ margin: '0 0 0.75rem' }}>{L.prompt}</p>
      <div className="form-group">
        <label htmlFor="resend-verification-email">{L.email}</label>
        <input
          type="email"
          id="resend-verification-email"
          name="email"
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={sending}
        />
      </div>
      <button type="submit" className="btn btn-secondary" disabled={sending}>
        {sending ? L.sending : L.send}
      </button>
      {status === 'sent' && (
        <p role="status" style={{ marginTop: '0.75rem', color: 'var(--gray-700)' }}>{L.sent}</p>
      )}
      {status === 'error' && error && (
        <p role="alert" style={{ marginTop: '0.75rem', color: 'var(--error)' }}>{error}</p>
      )}
    </form>
  );
};

export default ResendVerification;
